/**
 * 确认对话框 — 以命令式 API 调用的模态确认框，返回 Promise<boolean>。
 *
 * 设计决策：
 * - 不使用 Lit 组件声明，而是通过原生 DOM 操作动态创建/销毁，
 *   这样调用方无需在模板中预声明元素，使用更简洁。
 * - 样式通过一次性注入 <style> 标签实现，CSS 类名以 `cd-` 前缀避免冲突。
 * - 支持 `danger`（红色危险操作）和 `warning`（琥珀色警告操作）两种视觉变体。
 * - 点击遮罩层外部或按 Escape 键可取消（返回 false）。
 *
 * 用法示例:
 *   if (!(await showConfirm('确定删除吗？'))) return;
 *   if (await showConfirm('确认提交？', { variant: 'warning', confirmText: '提交' })) { ... }
 *
 * 主要导出：`showConfirm` 函数、`ConfirmOptions` 接口
 */
/**
 * 确认对话框配置选项
 */
interface ConfirmOptions {
  /** 对话框标题，默认 '确认操作' */
  title?: string;
  /** 确认按钮文案，默认 '确定' */
  confirmText?: string;
  /** 取消按钮文案，默认 '取消' */
  cancelText?: string;
  /** 视觉变体：danger（红色，危险操作）或 warning（琥珀色，警告操作），默认 danger */
  variant?: 'danger' | 'warning';
}

// Design tokens matching tailwind.css @theme — 一次性创建的 <style> 元素，按需注入 document.head
const style = document.createElement('style');
style.id = 'confirm-dialog-css';
style.textContent = `
  .cd-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
    animation: cd-fade-in 150ms ease-out;
  }
  .cd-card {
    background: #ffffff;
    border-radius: 24px;
    box-shadow: 0 20px 25px rgba(0, 0, 0, 0.08), 0 8px 10px rgba(0, 0, 0, 0.04);
    width: 100%;
    max-width: 28rem;
    max-height: 90vh;
    overflow-y: auto;
    border: 1px solid #e0e1e6;
    margin: 0 1rem;
    animation: cd-scale-in 150ms ease-out;
  }
  .cd-body { padding: 1.5rem; }
  .cd-icon-wrap {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cd-icon-danger { background: rgba(235, 142, 144, 0.1); color: #eb8e90; }
  .cd-icon-warning { background: rgba(171, 100, 0, 0.1); color: #ab6400; }
  .cd-title {
    margin: 0;
    font-weight: 600;
    font-size: 1.125rem;
    color: #1c2024;
  }
  .cd-message {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.625;
    color: #60646c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cd-row { display: flex; align-items: flex-start; gap: 1rem; }
  .cd-flex-1 { flex: 1; min-width: 0; }
  .cd-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
  .cd-btn {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 8px;
    transition: background-color 150ms;
    cursor: pointer;
    border: none;
  }
  .cd-btn-cancel {
    background: transparent;
    color: #60646c;
    border: 1px solid #e0e1e6;
  }
  .cd-btn-cancel:hover { background: #f0f0f3; }
  .cd-btn-confirm { color: #ffffff; }
  .cd-btn-confirm.cd-variant-danger { background: #eb8e90; }
  .cd-btn-confirm.cd-variant-danger:hover { background: #dc2626; }
  .cd-btn-confirm.cd-variant-warning { background: #ab6400; }
  .cd-btn-confirm.cd-variant-warning:hover { background: #8a5000; }
  @keyframes cd-fade-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes cd-scale-in { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
`;

/**
 * 显示确认对话框，返回 Promise<boolean>。
 * 用户点击确认按钮时 resolve(true)，点击取消/遮罩/Escape 时 resolve(false)。
 *
 * @param message - 对话框提示消息
 * @param options - 可选配置（标题、按钮文案、变体）
 * @returns Promise，resolve 为 true 表示用户确认，false 表示用户取消
 */
export function showConfirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
  const {
    title = '确认操作',
    confirmText = '确定',
    cancelText = '取消',
    variant = 'danger',
  } = options;

  // Ensure stylesheet is in document — 仅首次调用时注入样式
  if (!document.getElementById('confirm-dialog-css')) {
    document.head.appendChild(style);
  }

  return new Promise((resolve) => {
    // 根据变体选择图标和按钮样式类名
    const iconClass = variant === 'danger' ? 'cd-icon-danger' : 'cd-icon-warning';
    const btnClass = variant === 'danger' ? 'cd-variant-danger' : 'cd-variant-warning';

    // 创建遮罩层和卡片
    const overlay = document.createElement('div');
    overlay.className = 'cd-overlay';

    const card = document.createElement('div');
    card.className = 'cd-card';
    card.innerHTML = `
      <div class="cd-body">
        <div class="cd-row">
          <div class="cd-icon-wrap ${iconClass}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:1.25rem;height:1.25rem;">
              <path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86 3.36 15.86a2 2 0 0 0 1.71 3h13.86a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            </svg>
          </div>
          <div class="cd-flex-1">
            <h3 class="cd-title"></h3>
            <p class="cd-message"></p>
          </div>
        </div>
        <div class="cd-actions">
          <button class="cd-btn cd-btn-cancel"></button>
          <button class="cd-btn cd-btn-confirm ${btnClass}"></button>
        </div>
      </div>
    `;
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.querySelector<HTMLElement>('.cd-title')!.textContent = title;
    card.querySelector<HTMLElement>('.cd-message')!.textContent = message;
    card.querySelector<HTMLButtonElement>('.cd-btn-cancel')!.textContent = cancelText;
    card.querySelector<HTMLButtonElement>('.cd-btn-confirm')!.textContent = confirmText;

    /**
     * 清理函数 — 移除事件监听、从 DOM 移除对话框元素、resolve Promise。
     * @param result - 用户操作结果：true 为确认，false 为取消
     */
    const cleanup = (result: boolean) => {
      overlay.removeEventListener('click', onOverlayClick);
      document.removeEventListener('keydown', onKeydown);
      overlay.remove();
      resolve(result);
    };

    // 记录 mousedown 是否发生在遮罩层本身（而非卡片内容上），
    // 用于实现「点击遮罩层外部关闭」但避免拖拽选中文本时误触
    let overlayMousedown = false;
    overlay.addEventListener('mousedown', (e: Event) => {
      overlayMousedown = e.target === overlay;
    });

    // 仅当 mousedown 和 click 都发生在遮罩层本身时才关闭（防止文本拖拽误触）
    const onOverlayClick = (e: Event) => {
      if (e.target === overlay && overlayMousedown) cleanup(false);
    };
    overlay.addEventListener('click', onOverlayClick);

    // 按 Escape 键取消
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cleanup(false);
    };
    document.addEventListener('keydown', onKeydown);

    // 绑定取消和确认按钮的点击事件
    card.querySelector('.cd-btn-cancel')!.addEventListener('click', () => cleanup(false));
    card.querySelector('.cd-btn-confirm')!.addEventListener('click', () => cleanup(true));

    // 将卡片放入遮罩层，再将遮罩层挂载到 body
    overlay.appendChild(card);
    document.body.appendChild(overlay);
  });
}
